import React, { ReactElement, useState, useEffect } from 'react';
import { OrganizationSummary } from 'services/api';
import { Image } from 'components/lib';
import { Box, Typography, Checkbox, CircularProgress } from '@mui/material';
import { Button } from '../../../../ui/Button';

interface SelectionContentProps {
  modalType: 'organizations' | 'approval-categories' | null;
  onApply: (selectedIds: string[]) => void;
  onCancel: () => void;
  selectedIds: string[];
  categories?: Array<{ id: string; name: string }>;
  organizations?: OrganizationSummary[];
  isLoading?: boolean;
}

export const SelectionContent = ({
  modalType,
  onApply,
  onCancel,
  selectedIds,
  categories,
  organizations = [],
  isLoading = false,
}: SelectionContentProps): ReactElement => {
  const [selected, setSelected] = useState<string[]>(selectedIds);
  const [search, setSearch] = useState('');
  const [activeTab, setActiveTab] = useState<'all' | 'selected'>('all');

  useEffect(() => {
    setSelected(selectedIds);
  }, [selectedIds]);

  const isOrganizationsModal = modalType === 'organizations';
  const isApprovalCategoriesModal = modalType === 'approval-categories';

  const filteredOrganizations = organizations.filter(org =>
    org.name.toLowerCase().includes(search.toLowerCase()),
  );

  const selectedOrganizationsList = organizations.filter(org =>
    selected.includes(org.id),
  );

  const filteredCategories =
    categories?.filter(category =>
      category.name.toLowerCase().includes(search.toLowerCase()),
    ) || [];

  const selectedCategoriesList =
    categories?.filter(category => selected.includes(category.id)) || [];

  const handleToggleItem = (itemId: string) => {
    setSelected(prev =>
      prev.includes(itemId)
        ? prev.filter(id => id !== itemId)
        : [...prev, itemId],
    );
  };

  const handleApply = () => {
    onApply(selected);
  };

  const getItemsList = () => {
    if (isOrganizationsModal) {
      return activeTab === 'all'
        ? filteredOrganizations
        : selectedOrganizationsList;
    }

    if (isApprovalCategoriesModal)
      return activeTab === 'all' ? filteredCategories : selectedCategoriesList;

    return [];
  };

  const renderItem = (
    item: OrganizationSummary | { id: string; name: string },
  ) => {
    if (isOrganizationsModal && 'logoUrl' in item) {
      return (
        <Box display="flex" alignItems="center" ml={12}>
          <Image
            src={item.logoUrl}
            alt={item.name}
            width={32}
            height={32}
            style={{ borderRadius: '4px' }}
          />
          <Typography sx={{ ml: 12, fontWeight: 500 }}>{item.name}</Typography>
        </Box>
      );
    }
    if (isApprovalCategoriesModal) {
      return (
        <Typography sx={{ ml: 12, fontWeight: 500 }}>{item.name}</Typography>
      );
    }
    return null;
  };

  return (
    <Box sx={{ width: '600px', maxWidth: '80vw' }}>
      {/* Clear Selections Button - Only show when there are selections */}
      {selected.length > 0 && (
        <Box mb={20} display="flex" justifyContent="flex-start">
          <Button
            variant="outline"
            onClick={() => {
              setSelected([]);
            }}
            hoverEffect="underline"
            sx={{ textDecoration: 'underline', padding: 0 }}
          >
            Clear Selections
          </Button>
        </Box>
      )}
      {isOrganizationsModal && (
        <Box display="flex" borderBottom="1px solid #e0e0e0" mb={20}>
          <Box
            sx={{
              padding: '12px 24px',
              cursor: 'pointer',
              borderBottom: activeTab === 'all' ? '2px solid #1976d2' : 'none',
              color: activeTab === 'all' ? '#000' : '#666',
            }}
            onClick={() => setActiveTab('all')}
          >
            <Typography sx={{ fontWeight: 500 }}>All</Typography>
          </Box>
          <Box
            sx={{
              padding: '12px 24px',
              cursor: 'pointer',
              borderBottom:
                activeTab === 'selected' ? '2px solid #1976d2' : 'none',
              color: activeTab === 'selected' ? '#000' : '#666',
            }}
            onClick={() => setActiveTab('selected')}
          >
            <Typography sx={{ fontWeight: 500 }}>Selected</Typography>
          </Box>
        </Box>
      )}

      {/* Search */}
      <Box mb={20}>
        <input
          type="text"
          placeholder={`Search by ${
            isOrganizationsModal ? 'name' : 'category'
          }`}
          value={search}
          onChange={e => setSearch(e.target.value)}
          style={{
            width: '100%',
            padding: '12px 16px',
            border: '1px solid #e0e0e0',
            borderRadius: '8px',
            fontSize: '14px',
            outline: 'none',
          }}
        />
      </Box>

      {/* Items List */}
      <Box
        sx={{
          maxHeight: 300,
          overflowY: 'auto',
          border: '1px solid #e0e0e0',
          borderRadius: '8px',
          position: 'relative', // Needed for loader positioning
        }}
      >
        {isLoading ? (
          <Box
            display="flex"
            justifyContent="center"
            alignItems="center"
            sx={{ height: '100%', minHeight: '150px' }}
          >
            <CircularProgress />
          </Box>
        ) : (
          getItemsList().map(item => (
            <Box
              key={item.id}
              display="flex"
              alignItems="center"
              padding="12px 16px"
              onClick={e => {
                e.stopPropagation();
                handleToggleItem(item.id);
              }}
              sx={{
                cursor: 'pointer',
                borderBottom: '1px solid #f0f0f0',
                '&:last-child': { borderBottom: 'none' },
                '&:hover': { backgroundColor: '#f8f8f8' },
              }}
            >
              <Checkbox checked={selected.includes(item.id)} />
              {renderItem(item)}
            </Box>
          ))
        )}
      </Box>

      {/* Action Buttons */}
      <Box mt={30} display="flex" flexDirection="column" alignItems="center">
        <Button
          variant="vibrant"
          onClick={handleApply}
          minWidth={320}
          minHeight={47}
        >
          Apply
        </Button>
        <Button
          variant="outline"
          onClick={onCancel}
          hoverEffect="underline"
          sx={{ mt: 12, textDecoration: 'underline' }}
        >
          Cancel
        </Button>
      </Box>
    </Box>
  );
};
